import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import Material from 'components/property-settings/material/material';
import Shade from 'components/property-settings/shade/shade';
import FacialLoop from 'components/property-settings/facial-loop/facial-loop';
import ClaspTypes from 'components/property-settings/clasp-types/clasp-types';
import ContactType from 'components/property-settings/contact-type/contact-type';
import ClaspMaterial from 'components/property-settings/clasp-material/clasp-material';
import OcclusionType from 'components/property-settings/occlusion-type/occlusion-type';
import MetalDesign from 'components/property-settings/metal-design/metal-design';
import PonticDesign from 'components/property-settings/pontic-design/pontic-design';
import SpringTypes from 'components/property-settings/spring-types/spring-types';
import OrthodonticsColor from 'components/property-settings/orthodontics-color/orthodontics-color';
import ExpansionScrew from 'components/property-settings/expansion-screw/expansion-screw';
import Protrusion from 'components/property-settings/protrusion/protrusion';
import ClaspTeeth from 'components/property-settings/clasp-teeth/clasp-teeth';
import SpringTeeth from 'components/property-settings/spring-teeth/spring-teeth';
import Retainment from 'components/property-settings/retainment/retainment';
import TestingSteps from 'components/property-settings/testing-steps/testing-steps';
import ImplantSteps from 'components/property-settings/implant-steps/implant-steps';
import DentureSteps from 'components/property-settings/denture-steps/denture-steps';
import AddConstructionProperty from 'components/add-construction-property/add-construction-property.component';
import { AvailableConstructionProperties, ConstructionProperty } from 'models/property';
import { Box } from '@mui/material';
import { LabConstuction } from 'models/order-input/lab-order';
import isEqual from 'lodash/isEqual';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import AddPropertyButton from './add-property-button.component';

type ConstructionPropertiesProps = {
    isActionBasedClinic: boolean;
    construction: LabConstuction;
    onSettingsChange?: (usedProperties: AvailableConstructionProperties, settings: ConstructionProperty) => void;
};

const ConstructionProperties: FC<ConstructionPropertiesProps> = ({
    isActionBasedClinic,
    construction,
    onSettingsChange,
}: ConstructionPropertiesProps) => {
    const { t: translate } = useTranslation(['orders']);
    const { order, setOrder } = useContext(LabOrderContext);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [usedProperties, setUsedProperties] = useState<AvailableConstructionProperties>(
        {} as AvailableConstructionProperties,
    );
    const [settings, setSettings] = useState<ConstructionProperty>({} as ConstructionProperty);

    useEffect(() => {
        if (!construction) return;
        if (!isEqual(construction.properties, settings) || !isEqual(construction.usedProperties, usedProperties)) {
            if (construction.properties && construction.usedProperties) {
                setSettings(() => construction.properties);
                setUsedProperties(() => construction.usedProperties);
            }
        }

        if (!construction.properties?.material && !construction.properties?.otherMaterial) {
            setUsedProperties((prev) => ({ ...prev, MATERIAL: !isActionBasedClinic }));
        }
    }, [construction, isActionBasedClinic]);

    const openAddPropertyModal = () => {
        setDialogOpen(true);
    };

    const closeAddPropertyModal = () => {
        setDialogOpen(false);
    };

    const handlePropertySelection = (propertyName: string) => {
        const updatedProps = { ...usedProperties, [propertyName]: true };
        setUsedProperties((prev) => ({ ...prev, ...updatedProps }));
        closeAddPropertyModal();
    };

    const handleSettingsChange = (propertyName: string, settingValue: string | boolean | { id: number }[]) => {
        const updatedSettings = { ...settings, [propertyName]: settingValue };
        setSettings((prev) => ({ ...prev, ...updatedSettings }));
        if (onSettingsChange) {
            onSettingsChange(usedProperties, updatedSettings);
        }
    };

    const handleShadeChange = (properties: { shade: string; color: string; colorSampling: boolean }) => {
        setSettings((prev) => ({ ...prev, ...properties }));
        if (onSettingsChange) {
            onSettingsChange(usedProperties, { ...settings, ...properties });
        }
    };

    const handleMaterialsChange = (properties: { material: string; otherMaterial: string }) => {
        setSettings((prev) => ({ ...prev, ...properties }));
        if (onSettingsChange) {
            onSettingsChange(usedProperties, { ...settings, ...properties });
        }
    };

    return (
        <Fragment>
            <Box flexGrow={1}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg={4}>
                        <Shade
                            shade={settings?.shade}
                            color={settings?.color}
                            colorSampling={settings?.colorSampling}
                            onChange={handleShadeChange}
                        />
                    </Grid>
                    {usedProperties.MATERIAL ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <Material
                                material={settings?.material}
                                otherMaterialValue={settings?.otherMaterial}
                                onChange={handleMaterialsChange}
                            />
                        </Grid>
                    ) : undefined}
                    {usedProperties.FACIAL_LOOP ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <FacialLoop facialLoop={settings?.facialLoop} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.CLASP_TYPES ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ClaspTypes claspType={settings?.claspType} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.CONTACT_TYPE ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ContactType contactType={settings?.contactType} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.METAL_DESIGN ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <MetalDesign design={settings?.design} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.PONTIC_DESIGN ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <PonticDesign ponticDesign={settings?.ponticDesign} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.SPRING_TYPES ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <SpringTypes springType={settings?.springType} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.CLASP_MATERIAL ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ClaspMaterial
                                claspMaterial={settings?.claspMaterial}
                                handleSettings={handleSettingsChange}
                            />
                        </Grid>
                    ) : undefined}
                    {usedProperties.RETAINMENT ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <Retainment retainment={settings?.retainment} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.CLASP_TEETH ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ClaspTeeth claspTeeth={settings?.claspTeeth} handleTeethSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.SPRING_TEETH ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <SpringTeeth
                                springTeeth={settings?.springTeeth}
                                handleTeethSettings={handleSettingsChange}
                            />
                        </Grid>
                    ) : undefined}
                    {usedProperties.ORTHODONTICS_COLORS ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <OrthodonticsColor
                                partOrthoColor={settings.orthColor}
                                handleSettings={handleSettingsChange}
                            />
                        </Grid>
                    ) : undefined}
                    {usedProperties.EXPANSION_SCREW ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ExpansionScrew
                                partExpansionScrew={settings.expansionScrew}
                                handleSettings={handleSettingsChange}
                            />
                        </Grid>
                    ) : undefined}
                    {usedProperties.PROTRUSION ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <Protrusion partProtrusion={settings.protrusion} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.OCCLUSION_TYPE ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <OcclusionType occlusion={settings?.occlusion} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.DENTURE_STEPS ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <DentureSteps dentureSteps={settings?.dentureSteps} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.IMPLANT_STEPS ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ImplantSteps implantSteps={settings?.implantSteps} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    {usedProperties.TESTING_STEPS ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <TestingSteps testingSteps={settings?.testingSteps} handleSettings={handleSettingsChange} />
                        </Grid>
                    ) : undefined}
                    <Grid item xs={12} md={6} lg={4}>
                        <AddPropertyButton
                            disableRipple={true}
                            onClick={openAddPropertyModal}
                            variant="extended"
                            aria-label="add"
                        >
                            <AddIcon />
                            <Box>{translate('ADD_CONSTRUCTION_PROPERTY')}</Box>
                        </AddPropertyButton>
                    </Grid>
                </Grid>
                <AddConstructionProperty
                    open={dialogOpen}
                    handlePropertySelection={handlePropertySelection}
                    handleClose={closeAddPropertyModal}
                />
            </Box>
        </Fragment>
    );
};

export default ConstructionProperties;
